/**
 * Which screens float the corner chat bubble ON A PHONE.
 *
 * On a phone the bubble floats OVER the content rather than beside it, so on a screen you would never
 * start a chat from it is simply one more thing covering the last row. The native apps float it on
 * exactly three screens; web mounts `FloatingChat` app-wide and so showed it on every route, which is
 * the divergence this closes.
 *
 * The three, with their native twins:
 *
 *   | pathname              | screen              | iOS                    | Android              |
 *   |-----------------------|---------------------|------------------------|----------------------|
 *   | `/`                   | Catch-up home       | `SymphonyHomeView`     | `Pages.kt` home      |
 *   | `/agents`             | Your Agents         | `AgentsScreen`         | `Pages.kt` agents    |
 *   | `/workspace/[slug]`   | a Space's Dashboard | `WorkspaceScreen`      | `Workspace.kt`       |
 *
 * `/workspace/[slug]/goals` is deliberately NOT listed: Goals left the Space's tab strip for the
 * top-level `/goals` screen (mirroring Tasks), so this route is now a bare redirect onto `/goals` — a
 * screen that isn't in this allowlist either, matching Tasks (`/tasks` has no bubble). Both native apps
 * dropped their old in-tab FAB-hiding branch along with the retired Goals tab, for the same reason.
 *
 * **Wide is deliberately not gated by this.** On a desktop the bubble sits in the corner beside the
 * content, covering nothing, and it is the only always-available door into the side dock — so
 * `FloatingChat` keeps rendering it on every route there. This predicate is asked only on narrow.
 *
 * The chat is still reachable from the screens that lose it, the same way it is on the phones: the
 * drawer's "Talk to <product>" destination (`/?new=1`) and its Recent Chats rows open it from anywhere.
 */

/**
 * Next.js `router.pathname` PATTERNS (dynamic segments included verbatim, e.g. `[slug]`) — not concrete
 * URLs. Matching the pattern rather than the resolved path is what makes "every Space's dashboard, no
 * Space's Goals tab" one entry instead of a regex.
 */
export const MOBILE_CHAT_LAUNCHER_PATHNAMES: readonly string[] = [
  '/',
  '/agents',
  '/workspace/[slug]',
  // "Agents at work" was merged into the Dashboard tab and this route is now a `router.replace` onto it
  // (see `pages/workspace/[slug]/agents.tsx`). It IS the dashboard, one redirect away, so it carries the
  // bubble too — otherwise landing on a shipped deeplink replays the bubble's entrance animation the
  // instant the redirect lands.
  '/workspace/[slug]/agents',
];

/**
 * Whether a phone shows the chat bubble on [pathname]. Pure, so the route list is testable without a
 * router: pass `router.pathname` (the pattern form — `/workspace/[slug]`, not `/workspace/my-space`).
 *
 * Unknown routes are OUT, which is the safe default for a floating overlay: a new screen has to opt in
 * by naming itself here, and forgetting to means the bubble is missing rather than covering something.
 */
export function showsMobileChatLauncher(pathname: string): boolean {
  // A trailing slash is not a different screen. Next normalizes this away, so it is only ever a guard
  // against a hand-built caller ('/agents/' must not silently fall through to "no bubble").
  const normalized = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return MOBILE_CHAT_LAUNCHER_PATHNAMES.includes(normalized);
}
